import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import {
  useCreateTest,
  useDeleteTest,
  useReorderTests,
  useSetTestActive,
  useTests,
  useUpdateTest,
  type CatalogueTest,
  type TestInput,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { configLabel } from '@/lib/labels';
import { Button, Spinner } from '@/ui/Button';
import { Chip } from '@/ui/Chip';
import { Field, SelectInput, TextInput } from '@/ui/Field';
import { Notice, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Sheet, SheetRow } from '@/ui/Sheet';
import { Reorder } from '@/ui/Reorder';
import { moveItem } from '@/lib/reorder';

const DIMENSIONS = ['technical', 'tactical', 'physical', 'mental', 'social', 'goalkeeping'];

const BLANK: TestInput = {
  label: '',
  dimension: 'physical',
  unit: '',
  direction: 'lower',
  minValue: null,
  maxValue: null,
};

/**
 * The club's own list of tests.
 *
 * `test_item` was always an org-owned table with a key, a unit, a direction and
 * bounds — and no way to write to it, so adding a test took a commit, a seed
 * version bump and a deploy. The product says configuration is data; until this
 * screen that was true for us and not for the coach (PP-068).
 *
 * It lives beside the club's crest and its positions rather than inside
 * Development, because this is the shape of the club — the thing every testing
 * day afterwards is drawn from — not a thing you do on a Tuesday.
 */
export function TestCatalogue() {
  const t = useTranslations();
  const tests = useTests();
  const create = useCreateTest();
  const update = useUpdateTest();
  const setActive = useSetTestActive();
  const remove = useDeleteTest();
  const reorder = useReorderTests();
  const { busy, formError, fieldError, submit, setFormError } = useAuthForm();

  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TestInput>(BLANK);

  /*
    The order on screen, held locally so a move lands under the thumb that made
    it rather than a round trip later. Re-seeded only when the set of tests
    changes — not on every refetch, or saving a reorder would fight the next
    one.
  */
  const rows = useMemo(() => tests.data ?? [], [tests.data]);
  const [ids, setIds] = useState<string[]>([]);
  const signature = rows.map((r) => r.id).sort().join(',');
  const seeded = useRef('');
  useEffect(() => {
    if (seeded.current === signature) return;
    seeded.current = signature;
    setIds(rows.map((r) => r.id));
  }, [signature, rows]);

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const ordered = ids.map((x) => byId.get(x)).filter((x): x is CatalogueTest => Boolean(x));
  const liveCount = rows.filter((r) => r.isActive).length;

  const startAdd = () => {
    setEditingId(null);
    setDraft(BLANK);
    setFormError(null);
    setOpenForm(true);
  };

  const startEdit = (row: CatalogueTest) => {
    setEditingId(row.id);
    setDraft({
      label: configLabel(row, t),
      dimension: row.dimension,
      unit: row.unit ?? '',
      direction: row.direction,
      minValue: row.minValue,
      maxValue: row.maxValue,
    });
    setFormError(null);
    setOpenForm(true);
  };

  const save = async () => {
    const ok = await submit(async () => {
      if (editingId) await update.mutateAsync({ id: editingId, input: draft });
      else await create.mutateAsync(draft);
    });
    if (ok) setOpenForm(false);
  };

  const move = (index: number, to: number) => {
    const next = moveItem(ids, index, to);
    setIds(next);
    reorder.mutate(next);
  };

  const numberField = (value: number | null | undefined) =>
    value === null || value === undefined ? '' : String(value);

  const parseNumber = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  };

  return (
    <Panel
      title={t('testCatalogue.title')}
      count={liveCount || undefined}
      collapsible
      defaultOpen={false}
      storeKey="settings.tests"
      summary={t('testCatalogue.summary')}
      action={
        <Button variant="quiet" onClick={openForm ? () => setOpenForm(false) : startAdd}>
          {openForm ? t('common.cancel') : t('testCatalogue.add')}
        </Button>
      }
    >
      <p className="border-b border-line px-4 py-3 text-meta text-ink-muted">
        {t('testCatalogue.lead')}
      </p>

      {openForm ? (
        <form
          noValidate
          className="border-b border-line bg-surface-sunken"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Section
            title={editingId ? t('testCatalogue.edit') : t('testCatalogue.add')}
            hint={t('testCatalogue.formHint')}
          >
            {formError ? (
              <div className="mb-4">
                <Notice tone="risk">{formError}</Notice>
              </div>
            ) : null}

            {/* Said before the save, not after: a coach renaming "Yo-Yo IR1"
                is trading two translations for one word, and that is a choice
                rather than a surprise. */}
            {editingId && byId.get(editingId)?.seeded ? (
              <div className="mb-4">
                <Notice tone="info">{t('testCatalogue.renameWarning')}</Notice>
              </div>
            ) : null}

            <div className="grid gap-x-5 sm:grid-cols-2">
              <Field label={t('testCatalogue.label')} error={fieldError('label')}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={draft.label}
                    onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                    placeholder={t('testCatalogue.labelPlaceholder')}
                  />
                )}
              </Field>

              <Field label={t('testCatalogue.dimension')} error={fieldError('dimension')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={draft.dimension}
                    onChange={(e) => setDraft({ ...draft, dimension: e.target.value })}
                  >
                    {DIMENSIONS.map((d) => (
                      <option key={d} value={d}>
                        {t(`development.dimension.${d}`)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              <Field
                label={t('testCatalogue.unit')}
                hint={t('testCatalogue.unitHint')}
                error={fieldError('unit')}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    value={draft.unit}
                    onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                    placeholder={t('testCatalogue.unitPlaceholder')}
                  />
                )}
              </Field>

              <Field
                label={t('testCatalogue.direction')}
                hint={t('testCatalogue.directionHint')}
                error={fieldError('direction')}
              >
                {(p) => (
                  <SelectInput
                    {...p}
                    value={draft.direction}
                    onChange={(e) =>
                      setDraft({ ...draft, direction: e.target.value as 'higher' | 'lower' })
                    }
                  >
                    <option value="lower">{t('testCatalogue.lower')}</option>
                    <option value="higher">{t('testCatalogue.higher')}</option>
                  </SelectInput>
                )}
              </Field>

              <Field
                label={t('testCatalogue.min')}
                hint={t('testCatalogue.rangeHint')}
                optional
                error={fieldError('minValue')}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    step="any"
                    inputMode="decimal"
                    value={numberField(draft.minValue)}
                    onChange={(e) => setDraft({ ...draft, minValue: parseNumber(e.target.value) })}
                  />
                )}
              </Field>

              <Field
                label={t('testCatalogue.max')}
                optional
                error={fieldError('maxValue')}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    step="any"
                    inputMode="decimal"
                    value={numberField(draft.maxValue)}
                    onChange={(e) => setDraft({ ...draft, maxValue: parseNumber(e.target.value) })}
                  />
                )}
              </Field>
            </div>

            <div className="mt-2 flex gap-2">
              <Button type="submit" variant="primary" busy={busy}>
                {t('common.save')}
              </Button>
              <Button variant="secondary" onClick={() => setOpenForm(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </Section>
        </form>
      ) : null}

      {tests.isLoading ? (
        <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
          <Spinner /> {t('common.loading')}
        </p>
      ) : (
        <Sheet>
          {ordered.map((row, i) => (
            <SheetRow key={row.id} wrap>
              <span className="min-w-0 flex-1">
                <span className={`block font-medium ${row.isActive ? '' : 'text-ink-faint'}`}>
                  {configLabel(row, t)}
                </span>
                <span className="block text-meta text-ink-muted">
                  {t(`development.dimension.${row.dimension}`)}
                  {row.unit ? ` · ${row.unit}` : ''}
                  {' · '}
                  {row.direction === 'lower'
                    ? t('testing.lowerIsBetter')
                    : t('testing.higherIsBetter')}
                  {row.minValue !== null && row.maxValue !== null ? (
                    <span className="tabular" dir="ltr">
                      {` · ${row.minValue} – ${row.maxValue}`}
                    </span>
                  ) : null}
                </span>
                {!row.isActive ? (
                  <span className="mt-1 block">
                    <Chip muted>{t('testCatalogue.retired')}</Chip>
                  </span>
                ) : null}
              </span>

              <span className="flex shrink-0 flex-wrap items-center gap-1">
                <Reorder
                  index={i}
                  count={ordered.length}
                  label={configLabel(row, t)}
                  onMove={(to) => move(i, to)}
                />
                <Button variant="quiet" onClick={() => startEdit(row)}>
                  {t('testCatalogue.edit')}
                </Button>
                <Button
                  variant="quiet"
                  onClick={() => setActive.mutate({ id: row.id, isActive: !row.isActive })}
                >
                  {row.isActive ? t('testCatalogue.retire') : t('testCatalogue.restore')}
                </Button>
                {/* Only where it is honest. A test with a season of times
                    against it cannot be deleted — those numbers would lose
                    their name — so the screen does not offer it. */}
                {!row.hasResults ? (
                  <Button variant="quiet" onClick={() => remove.mutate(row.id)}>
                    {t('testCatalogue.delete')}
                  </Button>
                ) : null}
              </span>
            </SheetRow>
          ))}
        </Sheet>
      )}

      <p className="border-t border-line px-4 py-3 text-meta text-ink-faint">
        {t('testCatalogue.retiredNote')}
      </p>
    </Panel>
  );
}
